import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';
import { finalize, map, mergeMap, shareReplay } from 'rxjs/operators';
import { Pagination } from '../pagination';
import { environment } from '@env/environment';
import { StorageService } from '@services';

export const ApiCourseContextMapping = new Map<string, string>([
    ['course_instance', 'course'],
    ['traject', 'traject'],
    ['external_content', 'external_content'],
    ['external_video', 'external_video'],
]);

@Injectable()
export class ContentProvider {

    // Deduplica llamadas concurrentes al mismo endpoint de meta con los mismos parámetros:
    // watch.component y topbar-filters (embebido en su propio template) llaman getMeta('catalogue')
    // de forma independiente en el mismo ngOnInit — sin esto, /catalogue/meta se dispara 2 veces
    // por carga de página. Se comparte el mismo Observable mientras está en vuelo; una vez
    // completa, se limpia para no servir datos obsoletos en una carga posterior.
    private metaRequestCache = new Map<string, Observable<any>>();

    constructor(private http: HttpClient, private storageService: StorageService) { }

    private cachedMetaGet(url: string, params?: any): Observable<any> {
        const cacheKey = `${url}?${JSON.stringify(params || {})}`;
        if (!this.metaRequestCache.has(cacheKey)) {
            const request$ = this.http.get(url, { params }).pipe(
                shareReplay(1),
                finalize(() => this.metaRequestCache.delete(cacheKey))
            );
            this.metaRequestCache.set(cacheKey, request$);
        }
        return this.metaRequestCache.get(cacheKey);
    }

    getContents(params: any): Observable<any> {
        const response = {
            pagination: {},
            contents: []
        };

        return this.http.get(`${environment.envVar.API_URL}/course-subscriptions`, { params }).pipe(
            map((data: any) => {
                if (!data || !Array.isArray(data.data)) {
                    return data ?? { data: [], pagination: {} };
                }
                data.data = data.data.map(content => {
                    if (!content || !content.data) { return content; }
                    content.data = {
                        ...content.data,
                        totalDuration: content.data.duration,
                        progress: content.data.progress ?? 0,
                        eventDuration: content.data.eventDuration ?? 0,
                        duration: content.data.duration ?? 0,
                        score: content.data.score ?? 0,
                    };
                    return content;
                });
                return data;
            }),
            map((data: any) => {
                response.pagination = new Pagination(data.pagination);
                response.contents = data.data;

                return response;
            })
        );
    }

    // Conteo de cursos/rutas completados vs totales para el dashboard "overview" — endpoint
    // liviano dedicado (agregados SQL en el backend) en vez de pedir per_page=1000 a
    // /course-subscriptions y descartar todo excepto type/progress de cada item.
    getCompletionStats(companyId: string | number): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/course-subscriptions/completion-stats`, {
            params: { company_id: companyId }
        }).pipe(
            map((data: any) => data?.data ?? { totalCourses: 0, completedCourses: 0, totalJourneys: 0, completedJourneys: 0 })
        );
    }

    getEvents(id: number, params?: any): Observable<any> {
        const response = {
            pagination: {},
            closest: [],
            events: []
        };

        return this.http.get(`${environment.envVar.API_URL}/company/${id}/events`, { params }).pipe(
            map((data: any) => {
                response.pagination = new Pagination(data.pagination);
                response.closest = data.closest;
                response.events = data.dates;

                return response;
            })
        );
    }

    getEventsCatalogue(params?: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/learning/events/catalogue`, { params }).pipe(
            map((data: any) => {
                return data;
            })
        );
    }

    getEventsSubscribed(params?: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/learning/events/contents/subscribed`, { params }).pipe(
            map((data: any) => {
                return data;
            })
        );
    }

    getSavedContents(params: any): Observable<any> {
        const response = {
            pagination: {},
            contents: []
        };

        return this.http.get(`${environment.envVar.API_URL}/saved-contents`, { params }).pipe(
            map((data: any) => {
                response.pagination = new Pagination(data.pagination);
                response.contents = data.savedContents;

                return response;
            })
        );
    }

    getOverview(params: any): Observable<any> {
        const response = {
            contents: [],
            externals: [],
            featured: []
        };

        return this.http.get(`${environment.envVar.API_URL}/catalogue/overview`, { params }).pipe(
            map((data: any) => {
                response.contents = data.courses;
                response.externals = data.externals;
                response.featured = data.featured;

                return response;
            })
        );
    }

    getThematics(id: number): Observable<any> {
        return forkJoin(
            this.http.get(`${environment.envVar.API_URL}/catalogue/skills/meta`, { params: { company_id: id, browse: 'internal' } as any }).pipe(
                map(data => data)
            ),
            this.http.get(`${environment.envVar.API_URL}/catalogue/skills/meta`, { params: { company_id: id, browse: 'external' } as any }).pipe(
                map(data => data)
            )
        );
    }

    getCatalogue(params: any): Observable<any> {
        const response = {
            catalogue: [],
            pagination: {}
        };

        return this.http.get(`${environment.envVar.API_URL}/catalogue`, { params }).pipe(
            map((data: any) => {
                response.catalogue = data.catalogue;
                response.pagination = new Pagination(data.pagination);

                return response;
            })
        );
    }

    getRemainingLanguages(params: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/catalogue/remaining-languages`, { params }).pipe(
            map((data: any) => {
                return data;
            })
        );
    }

    getCatalogueSkills(params: any): Observable<any> {
        const response = {
            catalogue: [],
            pagination: {}
        };

        return this.http.get(`${environment.envVar.API_URL}/catalogue/skills`, { params }).pipe(
            map((data: any) => {
                response.catalogue = data.catalogue;
                response.pagination = new Pagination(data.pagination);

                return response;
            })
        );
    }

    getCatalogueSkill(id: number, params?: any): Observable<any> {
        const response = {
            contents: [],
            levels: [],
            name: '',
            pagination: {}
        };

        return this.http.get(`${environment.envVar.API_URL}/catalogue/skill/${id}`, { params }).pipe(
            map((data: any) => {
                response.contents = data.contents;
                response.levels = data.levels;
                response.name = data.name;
                response.pagination = data.pagination;

                return response;
            })
        );
    }

    getCatalogueSkillLevel(id: number, level: number, params?: any): Observable<any> {
        const response = {
            contents: [],
            name: '',
            level: 0,
            pagination: {}
        };

        return this.http.get(`${environment.envVar.API_URL}/catalogue/skill/${id}/level/${level}`, { params }).pipe(
            map((data: any) => {
                response.contents = data.contents;
                response.name = data.name;
                response.level = data.level;
                response.pagination = new Pagination(data.pagination);

                return response;
            })
        );
    }

    getOnboardingContents(companyId: any, params?: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/admin/companies/${companyId}/users/contents`, { params }).pipe(
            map((data: any) => data)
        );
    }

    getOnboardingContentsMeta(companyId: any, params?: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/admin/companies/${companyId}/users/contents/meta`, { params }).pipe(
            map((data: any) => data.meta)
        );
    }

    getOnboardingContentSessions(companyId: any, contentId: any, params?: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/admin/companies/${companyId}/users/contents/${contentId}`, { params }).pipe(
            map((data: any) => data)
        );
    }

    getCoachOnboardingContents(companyId: any, userId: any, params?: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/companies/${companyId}/manage/users/${userId}/contents`, { params }).pipe(
            map((data: any) => data)
        );
    }

    getCoachOnboardingContentsMeta(companyId: any, userId: any, params?: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/companies/${companyId}/manage/users/${userId}/contents/meta`, { params }).pipe(
            map((data: any) => data.meta)
        );
    }

    getCoachOnboardingContentSessions(companyId: any, contentId: any, userId: any, params?: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/companies/${companyId}/manage/users/${userId}/contents/${contentId}`, { params }).pipe(
            map((data: any) => data)
        );
    }

    getMeta(type: string, params?: any): Observable<any> {
        let url = null;
        switch (type) {
            case 'content':
                url = `${environment.envVar.API_URL}/course-camp/count`;
                break;
            case 'catalogue':
                url = `${environment.envVar.API_URL}/catalogue/meta`;
                break;
            case 'saved-content':
                url = `${environment.envVar.API_URL}/saved-contents/count`;
                break;
        }
        
        // SE MUESTRA LAS AREAS DE CONOCIMIENTO EN MIS CURRSOS   'content', necesitamos combinar los datos con los skills del catálogo
        if (type === 'content') {
            return this.cachedMetaGet(url, params).pipe(
                mergeMap((contentData: any) => {
                    // Obtener también los skills del catálogo
                    return this.cachedMetaGet(`${environment.envVar.API_URL}/catalogue/meta`, params).pipe(
                        map((catalogueData: any) => {
                            // Combinar los datos: usar los datos de contenido pero agregar los skills del catálogo
                            const result = contentData.meta || {};
                            if (catalogueData.meta) {
                                result.contentSkills = catalogueData.meta.contentSkills;
                                result.languages = catalogueData.meta.languages || result.languages;
                                result.providers = catalogueData.meta.providers || result.providers;
                            }
                            return result;
                        })
                    );
                })
            );
        }

        return this.cachedMetaGet(url, params).pipe(
            map((data: any) => data.meta)
        );
    }

    addSavedContent(params: any): Observable<any> {
        return this.http.post(`${environment.envVar.API_URL}/learning/saved-contents`, params).pipe(
            map((data) => data)
        );
    }

    deleteSavedContent(params: any): Observable<any> {
        return this.http.delete(`${environment.envVar.API_URL}/learning/saved-contents`, { params }).pipe(
            map((data) => data)
        );
    }

    getTokenOneMentor(companyId: any, params: any): Observable<any> {
        return this.http.post(`${environment.envVar.API_URL}/common/companies/${companyId}/onementor/token`, params).pipe(
            map((data) => data) 
        );
    }

    getBoclip(params?: any): Observable<any> {
        return this.http.post(`${environment.envVar.API_URL}/catalogue/boclips`,  params ).pipe(
            map((data: any) => data)
        );
    }

    filterBoclip(params?: any): Observable<any> {
        return this.http.post(`${environment.envVar.API_URL}/catalogue/boclips/search`,  params ).pipe(
            map((data: any) => data)
        );
    }

    getUserTopRecomendations(companyId: any): Observable<any> {
        console.log('company id', companyId);
        return this.http.get(`${environment.envVar.API_URL}/learner/company/${companyId}/recommendations`).pipe(
            map((data: any) => data)
        );
    }

    getRecommendedCompanyContentsByLearner(companyId: any, experienceId: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/learning/company-recommended-contents/company/${companyId}/experience/${experienceId}`).pipe(
            map((data: any) => data)
        );
    }

    getRecommendedCompanyContents(companyId: any, page?: any, params?: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/admin/company-recommended-contents/content/${companyId}?per_page=10&page=${page}&order=desc` , { params }).pipe(
            map((data: any) => data)
        );
    }


    rateContent(companyId: any, params: any): Observable<any> {
        return this.http.post(`${environment.envVar.API_URL}/learner/company/${companyId}/ratings`, params).pipe(
            map((data: any) => data)
        );
    }

    rateContentUpdate(companyId: any, params: any, ratingId: any): Observable<any> {
        return this.http.put(`${environment.envVar.API_URL}/learner/company/${companyId}/ratings/${ratingId}`, params).pipe(
            map((data: any) => data)
        );
    }

    getContentRating(companyId: any, params: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/learner/company/${companyId}/ratings`, {params}).pipe(
            map((data: any) => data)
        );
    }

    getRatingsComments(companyId: any, params: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/learner/company/${companyId}/ratings/comments`,{params}).pipe(
            map((data: any) => data)
        );
    }

    getOverviewBannerInfo(companyId: any, experienceId: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/company/${companyId}/experience/${experienceId}/info-banner`).pipe(
            map((data: any) => data)
        );
    }

    getBanners(companyId: any, experienceId: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/company/${companyId}/experience/${experienceId}/info-banner`).pipe(
            map((data: any) => data)
        );
    }

    createBannerInfo(companyId: any, experienceId: any, params: any): Observable<any> {
        return this.http.post(`${environment.envVar.API_URL}/company/${companyId}/experience/${experienceId}/info-banner`, params).pipe(
            map((data: any) => data)
        );
    }

    updateBannerInfo(companyId: any, experienceId: any, params: any, informativeBannerId: any): Observable<any> {
        return this.http.put(`${environment.envVar.API_URL}/company/${companyId}/experience/${experienceId}/info-banner/${informativeBannerId}`, params).pipe(
            map((data: any) => data)
        );
    }

    deleteBannerInfo(companyId: any, experienceId: any, informativeBannerId: any): Observable<any> {
        return this.http.delete(`${environment.envVar.API_URL}/company/${companyId}/experience/${experienceId}/info-banner/${informativeBannerId}`).pipe(
            map((data: any) => data)
        );
    }

    getBannerDuration(companyId: any, experienceId: any): Observable<any> {
        return this.http.get(`${environment.envVar.API_URL}/company/${companyId}/experience/${experienceId}/banner-duration`).pipe(
            map((data: any) => data)
        );
    }

    updateBannerDuration(companyId: any, experienceId: any, params: any): Observable<any> {
        return this.http.put(`${environment.envVar.API_URL}/company/${companyId}/experience/${experienceId}/banner-duration`, params).pipe(
            map((data: any) => data)
        );
    }    
}
